import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { AssetGroupDto } from '@assetaccountant/api/models/asset-group.dto';
import { RegisterV2Api } from '@assetaccountant/api/services/register-v2.api';
import { NewAssetgroupPanelComponent } from '@assetaccountant/assetgroups/components/new-assetgroup-panel/new-assetgroup-panel.component';
import { SidePanelModel } from '@assetaccountant/assets/models/side-panel-model';
import { AssetGroupsService } from '@assetaccountant/core/services/assetgroups-service';
import { tap } from 'rxjs/operators';
import { SidePanelComponent } from '../side-panel/side-panel.component';

@Component({
  selector: 'app-asset-group-selector',
  templateUrl: './asset-group-selector.component.html',
  standalone: false,
})
export class AssetGroupSelectorComponent implements OnInit, ControlValueAccessor {
  @Input() canAddNewGroup = false;
  @Input() currentGroupId?: number;

  @Output() newAssetGroup = new EventEmitter<AssetGroupDto>();

  @ViewChild('sidePanel') sidePanel: SidePanelComponent;

  model: number | null = null;

  assetGroups?: AssetGroupDto[];
  group?: AssetGroupDto;

  disabled = false;

  status: 'LOADING' | 'NONE' | 'ERRORED' | 'OK';

  private _onChange?: (val: number | null) => void;
  private _onTouched?: () => void;

  get showError() {
    return !!(this.control && this.control.invalid && this.control.touched);
  }

  constructor(
    private registerApi: RegisterV2Api,
    private assetGroupService: AssetGroupsService,
    @Self() @Optional() private control?: NgControl,
  ) {
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }

  ngOnInit() {
    this.fetchGroups();
  }

  fetchGroups() {
    this.status = 'LOADING';

    this.registerApi
      .listAssetGroups()
      .pipe(tap({ error: () => (this.status = 'ERRORED') }))
      .subscribe(({ assetGroups }) => {
        if (assetGroups?.length) {
          this.assetGroups = this.assetGroupService.sortAssetGroups(assetGroups);
          this.group = this.assetGroups.find(ag => ag.assetGroupId === this.model);
          this.status = 'OK';
        } else {
          this.status = 'NONE';
        }
      });
  }

  writeValue(value: number | null): void {
    this.model = value || null;
    this.group = this.assetGroups?.find(ag => ag.assetGroupId === this.model);
  }

  onChange(val: string | number | null) {
    this.group = this.assetGroups?.find(ag => ag.assetGroupId === this.model);
    if (val && isFinite(+val)) {
      this._onChange?.(+val);
    } else {
      this._onChange?.(null);
    }
  }

  onCreateAssetGroup() {
    const sidePanelModel = new SidePanelModel(NewAssetgroupPanelComponent, 'New Asset Group', {
      width: 'sm',
    });

    this.sidePanel.open(sidePanelModel, sidePanel => {
      sidePanel.cancel.subscribe(() => this.sidePanel.close());
      sidePanel.groupCreated.subscribe(newGroup => {
        this.newAssetGroup.emit(newGroup);
        this.fetchGroups();
        this.onChange(newGroup.assetGroupId);
        this.writeValue(newGroup.assetGroupId);
        this.sidePanel.close();
      });
    });
  }

  onTouched() {
    this._onTouched?.();
  }

  registerOnChange(fn: (val: number | null) => void): void {
    this._onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }
}
